import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Sensor, SensorGroup, SensorReadingType } from '@models';
import { AnalysisType, Comparator, EventType, TimerEventType, ActionType, SensorCondition, EventAction, MotionCondition } from '@models';
import { DashboardWizardState, NotificationConditionPreset, createDefaultSensorCondition, createDefaultTimerCondition, createDefaultMotionCondition } from '@models/Dashboard/dashboardWizard.model';
import { DashboardInfo } from '@models/Dashboard/dashboardInfo.model';
import { DeviceInfo } from '@models/Device/DeviceInfo';
import { SensorWithGroupList } from '@models/Sensor/sensorWithGroupList.model';
import { UserGroup } from '@models/User/usergroup.model';
import { SensorService } from '@services/sensor.service';
import { SensorGroupService } from '@services/sensorgroup.service';
import { CommonShared } from 'app/shared/classes/common';
import { ToastrService } from 'ngx-toastr';
import moment from 'moment';

// Steps 2-6 of the Notification & Action path. The steps build up the shared
// Event (state.notification.event) — which the container also binds to the
// embedded event editor on the right — mapping: trigger→eventType, sensors/group→
// sensorConditions[0].sensorGroup, reading type→sensorConditions[0].sensorReadingType,
// condition preset+threshold→analysis/comparator/threshold/window, action→eventActions[0].
// This pass builds only the "A change in sensor(s) reading values" (SensorBased) path.
@Component({
  standalone: false,
  selector: 'notification-wizard-flow',
  templateUrl: './notification-wizard-flow.component.html',
  styleUrls: ['./notification-wizard-flow.component.scss']
})
export class NotificationWizardFlowComponent implements OnInit, OnChanges {
  @Input() step = 2;
  @Input() state: DashboardWizardState;
  @Input() dashboards: DashboardInfo[] = [];
  @Input() associatedUserGroups: UserGroup[] = [];
  @Input() username = '';
  // Gateway data for the motion step's "Search and Select Gateway" dropdown.
  @Input() gatewayNames: string[] = [];
  @Input() devices: DeviceInfo[] = [];

  @Output() stepValidChange = new EventEmitter<boolean>();

  common = new CommonShared();

  // ngx-select-dropdown config for the step-7 access-group picker.
  readonly userGroupConfig: any = {
    displayKey: 'name',
    search: true,
    height: '220px',
    placeholder: 'Select Access Group',
    searchOnKey: 'name'
  };

  // Step 2 trigger options. Only "sensorChange" is enabled this pass.
  readonly triggerOptions: { key: string; label: string; enabled: boolean }[] = [
    { key: 'sensorChange', label: 'A change in sensor(s) reading values', enabled: true },
    { key: 'timer',        label: 'Timer-based trigger',                  enabled: true },
    { key: 'motion',       label: 'Motion from a camera',                 enabled: true }
  ];

  // Step 6 action options.
  readonly actionOptions: { key: string; label: string; enabled: boolean }[] = [
    { key: 'email',      label: 'Send email',             enabled: true },
    { key: 'image',      label: 'Capture an image',       enabled: true },
    { key: 'irrigation', label: 'Start an irrigation',    enabled: true },
    { key: 'task',       label: 'Run a task on a gateway', enabled: true }
  ];
  selectedAction = 'email';

  // Step 5 condition presets (label shown next to the threshold box).
  readonly conditionOptions: { key: NotificationConditionPreset; label: string }[] = [
    { key: 'ingestCount', label: 'Ingest count drops below threshold' },
    { key: 'avg60Below',  label: 'Average value for last 60 min falls below threshold' },
    { key: 'minUnder',    label: 'Min value from sensor(s) is under' }
  ];

  // Reference data for the step-3 tree.
  allSensors: Sensor[] = [];
  allSensorGroups: SensorGroup[] = [];
  sensorsLoaded = false;
  sensorsAndGroupsDisplayed: SensorWithGroupList[] = [];
  SensorsAndGroupsCols = [
    { field: 'checked', header: '', width: '10%' },
    { field: 'name', header: 'Sensors and Groups', width: '90%' }
  ];
  searchSensorsText = '';

  constructor(
    private sensorService: SensorService,
    private sensorGroupService: SensorGroupService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    // Seed the Event's trigger + action to match the default radios.
    this.event.eventType = this.n.triggerKind === 'timer' ? EventType.TimerBased : EventType.SensorBased;
    this.selectedAction = 'email';
    if (!this.action.destination) {
      this.action.destination = this.username;
    }
    this.loadSensorsAndGroups();
    this.recomputeValidity();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['step'] && !changes['step'].firstChange) {
      this.recomputeValidity();
    }
  }

  // ---- shorthands into the shared Event ----
  get n() { return this.state.notification; }
  get event() { return this.state.notification.event; }
  get condition(): SensorCondition { return this.event.sensorConditions[0]; }
  get motionCondition(): MotionCondition { return this.event.motionConditions[0]; }
  get action(): EventAction { return this.event.eventActions[0]; }

  get selectedTrigger(): string { return this.n.triggerKind; }

  // Ordered step keys for the chosen trigger. Step 1 is the container's type
  // picker; these cover steps 2..N. Timer skips sensors/readingType/condition.
  get stepKeys(): string[] {
    if (this.n.triggerKind === 'timer') { return ['trigger', 'timer', 'action', 'save']; }
    if (this.n.triggerKind === 'motion') { return ['trigger', 'motion', 'action', 'save']; }
    return ['trigger', 'sensors', 'readingType', 'condition', 'action', 'save'];
  }

  get currentStepKey(): string {
    return this.stepKeys[this.step - 2];
  }

  // ---------- step 2: trigger ----------

  selectTrigger(key: string): void {
    const opt = this.triggerOptions.find(o => o.key === key);
    if (!opt || !opt.enabled) { return; }

    this.n.triggerKind = key as 'sensorChange' | 'timer' | 'motion';

    // Seed only the condition array the chosen trigger uses, and clear the others.
    // A stale empty SensorCondition (sensorGroup undefined) would throw at
    // save-mapping, so sensorConditions must be cleared for timer/motion.
    if (key === 'timer') {
      this.event.eventType = EventType.TimerBased;
      this.event.sensorConditions = [];
      this.event.motionConditions = [];
      if (this.event.timerConditions.length === 0) {
        this.event.timerConditions = [createDefaultTimerCondition(this.event.id)];
      }
    } else if (key === 'motion') {
      this.event.eventType = EventType.MotionBased;
      this.event.sensorConditions = [];
      this.event.timerConditions = [];
      if (this.event.motionConditions.length === 0) {
        this.event.motionConditions = [createDefaultMotionCondition(this.event.id)];
      }
    } else {
      this.event.eventType = EventType.SensorBased;
      this.event.timerConditions = [];
      this.event.motionConditions = [];
      if (this.event.sensorConditions.length === 0) {
        this.event.sensorConditions = [createDefaultSensorCondition()];
      }
    }

    // Changing the trigger changes the step sequence (and the stepper count), so
    // re-gate forward progress from the current step.
    this.state.furthestStepReached = this.step;
    this.recomputeValidity();
  }

  // Motion step: a gateway was picked — resolve its serial from the device list
  // (the save maps deviceSerial directly, so it must be set here).
  onMotionGatewayChanged(): void {
    const mc = this.motionCondition;
    if (mc != undefined && mc.deviceName) {
      const device = this.devices.find(d => d.name === mc.deviceName);
      mc.deviceSerial = device != undefined ? device.serialNumber : '';
    }
    this.recomputeValidity();
  }

  // ---------- step 3: sensors/group (copy-adapted from the chart flow) ----------

  private loadSensorsAndGroups(): void {
    this.sensorService.getAllSensorsWithoutTypes().subscribe(
      (results: Sensor[]) => {
        if (results != null) {
          this.allSensors = results;
          this.allSensors.forEach(s => { if (!s.name) { s.name = s.uuid; } });
        }
        this.sensorsLoaded = true;
        this.sensorSearchTextChanged();
      },
      () => { this.sensorsLoaded = true; this.toastr.error('Error occurred while retrieving sensor list'); }
    );
    this.sensorGroupService.getSensorGroupsWithSensors().subscribe(
      (results: SensorGroup[]) => { if (results != null) { this.allSensorGroups = results; } this.sensorSearchTextChanged(); },
      () => this.toastr.error('Error occurred while retrieving sensor groups')
    );
  }

  onPresetChanged(): void {
    this.sensorSearchTextChanged();
  }

  private scopedSensors(): Sensor[] {
    const p = this.n.sensorPreset;
    if (p.allSensorsAndGroups) { return this.allSensors; }
    if (!p.assignedToMeOrGroup && !p.recentlyAddedOrModified) { return []; }
    return this.allSensors.filter(s => this.matchesScope(s.userName, s.userGroupId, s.modifiedDate));
  }

  private scopedGroups(): SensorGroup[] {
    const p = this.n.sensorPreset;
    if (p.allSensorsAndGroups) { return this.allSensorGroups; }
    if (!p.assignedToMeOrGroup && !p.recentlyAddedOrModified) { return []; }
    return this.allSensorGroups.filter(g => this.matchesScope(g.userName, g.userGroupId, g.modifiedDate));
  }

  private matchesScope(userName: string, userGroupId: number, modifiedDate: Date): boolean {
    const p = this.n.sensorPreset;
    let keep = false;
    if (p.assignedToMeOrGroup) {
      const mine = userName && this.username && userName.toLowerCase() === this.username.toLowerCase();
      const inGroup = this.associatedUserGroups.some(g => g.id === userGroupId);
      keep = keep || mine || inGroup;
    }
    if (p.recentlyAddedOrModified) {
      keep = keep || (modifiedDate ? moment(modifiedDate).isAfter(moment().subtract(30, 'days')) : false);
    }
    return keep;
  }

  sensorSearchTextChanged(): void {
    const isSelected = (row: SensorWithGroupList) =>
      row.checked || (row.isSensorGroup && (row.children ?? []).some(c => c.checked));
    const kept = this.sensorsAndGroupsDisplayed.filter(isSelected);
    const keptSensorIds = new Set(kept.filter(r => r.isSensor).map(r => r.id));
    const keptGroupIds = new Set(kept.filter(r => r.isSensorGroup).map(r => r.id));

    const search = (this.searchSensorsText || '').trim().toLowerCase();
    this.searchSensorsText = search;

    let sensors = this.scopedSensors().filter(x =>
      (x.name || '').toLowerCase().includes(search) || (x.uuid || '').toLowerCase().includes(search));
    sensors = sensors.slice(0, 500).filter(x => !keptSensorIds.has(x.id));

    let groups = this.scopedGroups().filter(x => (x.name || '').toLowerCase().includes(search));
    groups = groups.slice(0, 150).filter(x => !keptGroupIds.has(x.id));

    this.sensorsAndGroupsDisplayed = kept
      .concat(sensors.map(s => ({ id: s.id, name: s.name, isSensor: true, isSensorGroup: false, checked: false, count: 1 })))
      .concat(groups.map(g => this.makeGroupRow(g, [])));

    this.sensorsAndGroupsDisplayed.sort((a, b) => a.name.localeCompare(b.name));
  }

  private makeGroupRow(group: SensorGroup, checkedSensorIds: number[]): SensorWithGroupList {
    const children: SensorWithGroupList[] = (group.sensors ?? []).map(s => ({
      id: s.id, name: s.name, isSensor: true, isSensorGroup: false,
      checked: checkedSensorIds.includes(s.id), count: 1
    }));
    const checkedCount = children.filter(c => c.checked).length;
    return {
      id: group.id, name: group.name, isSensor: false, isSensorGroup: true,
      count: group.sensors != undefined ? group.sensors.length : 0,
      children, expanded: false,
      checked: children.length > 0 && checkedCount === children.length,
      indeterminate: checkedCount > 0 && checkedCount < children.length
    };
  }

  onGroupChecked(group: SensorWithGroupList): void {
    (group.children ?? []).forEach(c => c.checked = group.checked);
    group.indeterminate = false;
    this.sensorSelectionChanged();
  }

  onGroupChildChecked(group: SensorWithGroupList): void {
    const children = group.children ?? [];
    const checkedCount = children.filter(c => c.checked).length;
    group.checked = children.length > 0 && checkedCount === children.length;
    group.indeterminate = checkedCount > 0 && checkedCount < children.length;
    this.sensorSelectionChanged();
  }

  toggleGroupExpanded(group: SensorWithGroupList): void { group.expanded = !group.expanded; }
  groupCheckedCount(group: SensorWithGroupList): number { return (group.children ?? []).filter(c => c.checked).length; }

  toggleCheckedSensors(): void {
    if (this.sensorsAndGroupsDisplayed.length === 0) { return; }
    const newState = !this.sensorsAndGroupsDisplayed[0].checked;
    this.sensorsAndGroupsDisplayed.forEach(row => {
      row.checked = newState;
      if (row.isSensorGroup) { row.indeterminate = false; (row.children ?? []).forEach(c => c.checked = newState); }
    });
    this.sensorSelectionChanged();
  }

  sensorSelectionChanged(): void {
    this.updateSelectedSensors();
    this.applySelectionToCondition();
    this.loadReadingTypes();
    this.recomputeValidity();
  }

  private updateSelectedSensors(): void {
    const selectedSensors: Sensor[] = [];
    const selectedSensorGroups: SensorGroup[] = [];
    const addSensor = (id: number) => {
      const sensor = this.allSensors.find(s => s.id === id);
      if (sensor && !selectedSensors.some(s => s.id === sensor.id)) {
        sensor.displayedSensorName = this.common.getSensorDisplayName(sensor);
        selectedSensors.push(sensor);
      }
    };
    this.sensorsAndGroupsDisplayed.forEach(row => {
      if (row.isSensor && row.checked) { addSensor(row.id); }
      if (row.isSensorGroup) {
        const checkedChildren = (row.children ?? []).filter(c => c.checked);
        if (checkedChildren.length > 0) {
          const group = this.allSensorGroups.find(g => g.id === row.id);
          if (group) { selectedSensorGroups.push(group); }
          checkedChildren.forEach(c => addSensor(c.id));
        }
      }
    });
    this.n.selectedSensors = selectedSensors;
    this.n.selectedSensorGroups = selectedSensorGroups;
  }

  // Set the Event's sensor condition to watch the selection. A single whole group
  // is used directly; otherwise an in-memory group is built from the selected
  // sensors (resolved to a real group at the deferred save).
  private applySelectionToCondition(): void {
    const groups = this.n.selectedSensorGroups;
    const sensors = this.n.selectedSensors;
    let group: SensorGroup;
    if (groups.length === 1 && this.selectionIsWholeGroup(groups[0])) {
      group = groups[0];
    } else {
      group = {
        id: 0,
        name: sensors.length > 0 ? 'Selected sensors (' + sensors.length + ')' : '',
        sensors: sensors.slice()
      } as SensorGroup;
    }
    this.condition.sensorGroup = group;
    this.condition.sensorGroupID = group.id;
  }

  private selectionIsWholeGroup(group: SensorGroup): boolean {
    const memberIds = new Set((group.sensors ?? []).map(s => s.id));
    const selectedIds = new Set(this.n.selectedSensors.map(s => s.id));
    if (memberIds.size === 0 || memberIds.size !== selectedIds.size) { return false; }
    for (const id of selectedIds) { if (!memberIds.has(id)) { return false; } }
    return true;
  }

  // ---------- step 4: reading type (single) ----------

  private loadReadingTypes(): void {
    const ids = this.n.selectedSensors.map(s => s.id);
    if (ids.length === 0) { this.n.availableReadingTypes = []; return; }
    this.sensorService.getReadingTypesForSensors(ids, '').subscribe(
      (types: SensorReadingType[]) => {
        this.n.availableReadingTypes = (types ?? []).slice();
        this.common.sortArrayByTypeParm(this.n.availableReadingTypes);
        if (this.condition.sensorReadingType &&
            !this.n.availableReadingTypes.some(t => t.id === this.condition.sensorReadingType.id)) {
          this.condition.sensorReadingType = undefined;
        }
        this.recomputeValidity();
      },
      () => this.toastr.error('Error occurred while retrieving reading types')
    );
  }

  onReadingTypeChanged(): void { this.recomputeValidity(); }

  // ---------- step 5: condition preset + threshold ----------

  applyConditionPreset(preset: NotificationConditionPreset): void {
    this.n.conditionPreset = preset;
    const c = this.condition;
    this.event.eventTypeSelect = 'SensorMinutes';
    c.lastXMinutes = 60;
    c.numberOfSamples = 0;
    c.comparator = Comparator.lt;
    if (preset === 'ingestCount') { c.analysisType = AnalysisType.singlecount; }
    else if (preset === 'avg60Below') { c.analysisType = AnalysisType.average; }
    else if (preset === 'minUnder') { c.analysisType = AnalysisType.min; }
    this.recomputeValidity();
  }

  onThresholdChanged(): void { this.recomputeValidity(); }

  // ---------- step 6: action ----------

  selectAction(key: string): void {
    const opt = this.actionOptions.find(o => o.key === key);
    if (!opt || !opt.enabled) { return; }
    this.selectedAction = key;
    const a = this.action;
    if (key === 'email') { a.type = ActionType.Email; if (!a.destination) { a.destination = this.username; } }
    else if (key === 'image') { a.type = ActionType.Camera; }
    else if (key === 'irrigation') { a.type = ActionType.Irrigation; }
    else if (key === 'task') { a.type = ActionType.Task; }
    this.recomputeValidity();
  }

  onActionDetailChanged(): void { this.recomputeValidity(); }

  // ---------- step 7: save ----------

  onAccessGroupChanged(): void {
    const g = this.n.accessUserGroup;
    this.event.userGroupID = (g != null && g.id > -1) ? g.id : null;
    this.event.userGroup = g != null ? g.name : '';
    this.recomputeValidity();
  }

  onSaveFieldChanged(): void {
    this.recomputeValidity();
  }

  // True when "Add to new dashboard" is chosen and the name collides with an
  // existing dashboard (case-insensitive).
  get newDashboardNameTaken(): boolean {
    const name = (this.n.newDashboardName || '').trim().toLowerCase();
    if (this.n.dashboardTargetMode !== 'new' || name === '') { return false; }
    return this.dashboards.some(d => d.name && d.name.trim().toLowerCase() === name);
  }

  // ---------- validation ----------

  private recomputeValidity(): void {
    let valid = false;
    switch (this.currentStepKey) {
      case 'trigger':
        valid = true;   // a trigger is always selected (defaults to sensorChange)
        break;
      case 'sensors':
        valid = this.n.selectedSensors.length > 0;
        break;
      case 'readingType':
        valid = this.condition != undefined && this.condition.sensorReadingType != undefined;
        break;
      case 'condition':
        valid = this.n.conditionPreset != null && this.condition != undefined
          && this.condition.threshold != undefined && !isNaN(Number(this.condition.threshold));
        break;
      case 'timer':
        valid = this.event.timerConditions.length >= 1
          && this.event.timerConditions.every(t =>
            t.type === TimerEventType.Single ? !!t.timingSingle : !!t.timingRepeat);
        break;
      case 'motion':
        valid = this.event.motionConditions.length >= 1
          && this.event.motionConditions.every(m =>
            !!m.deviceName && m.sensitivity >= 1 && m.sensitivity <= 100 && m.postMotionSleepTime >= 1);
        break;
      case 'action':
        valid = !!this.selectedAction
          && (this.selectedAction !== 'email'
            || (!!this.action.destination && this.action.destination.trim().length > 0));
        break;
      case 'save':
        valid = !!this.event.name && this.event.name.trim().length > 0
          && (this.n.dashboardTargetMode === 'existing'
            ? this.n.targetDashboard != null
            : !!this.n.newDashboardName && this.n.newDashboardName.trim().length > 0 && !this.newDashboardNameTaken);
        break;
      default:
        valid = true;
    }
    this.stepValidChange.emit(valid);
  }
}
